import { YScaleType } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar.service';
import { Chart, Plugin, ScatterDataPoint } from 'chart.js';
import { Enums } from 'constants/Enums.constant';

export const AutoYMinMaxPlugin: Plugin = {
  id: 'auto-y-min-max',
  afterDataLimits: (chart: Chart<'bar', ScatterDataPoint[]>, {scale}) => {
    if (scale.axis === 'x') return;
    const {
      unitSystem,
      unitPressure,
      unitWind,
    } = chart.config.options.plugins['auto-y-min-max'];

    const xScales = Object.values(chart.config.options.scales).filter(s => s.axis === 'x');
    const xScaleMin = Math.min(...xScales.map(s => s.min as number).filter(m => !isNaN(m)));
    const xScaleMax = Math.max(...xScales.map(s => s.max as number).filter(m => !isNaN(m)));

    // Find min/max of all datata in all datasets
    const points = chart.config.data.datasets.filter(dataset => (dataset.yAxisID ?? 'y') === scale.id).map(d => {
      return d.data
        .filter(p => p.x >= xScaleMin && p.x <= xScaleMax)
        .map(point => point);
    }).flat();

    let data: number[] = points.map(point => point.y);

    // Add values of stacked bars if scale is "stacked"
    if (chart.config.options.scales[scale.id].stacked) {
      data = Object.values(points.reduce((acc, curr) => {
        acc[curr.x] = acc[curr.x] ? acc[curr.x] + curr.y : curr.y;
        return acc;
      }, {} as Record<number, number>));
    }

    const min = data.length > 0 ? Math.min(...data) : 0;
    const max = data.length > 0 ? Math.max(...data) : 10;

    let minAmplitude = null;
    switch (scale.id) {
      case YScaleType.TEMPERATURE:
      case YScaleType.RAIN:
        minAmplitude = UNIT_AMPLITUDES[scale.id][unitSystem];
        break;
      case YScaleType.SPEED:
        minAmplitude = UNIT_AMPLITUDES[scale.id][unitWind];
        break;
      case YScaleType.PRESSURE:
        minAmplitude = UNIT_AMPLITUDES[scale.id][unitPressure];
        break;
      default:
        minAmplitude = UNIT_AMPLITUDES[scale.id];
        break;
    }

    if (minAmplitude && max - min < minAmplitude) {
      const middle = (max + min) / 2;
      scale.min = middle - minAmplitude / 2;
      scale.max = middle + minAmplitude / 2;
    }
    else {

      scale.min = min - (max - min) * 0.1;
      scale.max = max + (max - min) * 0.1;

      // Handle case wehan all data have same value in graph
      if (scale.min === scale.max) {
        scale.min = scale.min * 0.9;
        scale.max = (scale.max === 0)
          ? 10
          : scale.max * 1.1;
      }
    }

    // Force scale minimum to be 0 if no values goes below 0
    if (scale.min < 0 && min >= 0) {
      scale.min = 0;
    }

    // Set scale min to 0 for all bar charts
    if ([YScaleType.ELECTRICITY, YScaleType.RAIN, YScaleType.WATER].includes(scale.id as YScaleType)) {
      scale.min = 0;
    }

    if (scale.id === YScaleType.PERCENTAGE && scale.max > 100) {
      scale.max = 100;
    }
  },
};

const UNIT_AMPLITUDES = {
  [YScaleType.SPEED]: {
    [Enums.WindUnit.KMH]: 50,
    [Enums.WindUnit.MPH]: 30,
    [Enums.WindUnit.MS]: 15,
    [Enums.WindUnit.BEAUFORT]: 10,
    [Enums.WindUnit.KNOT]: 50,
  },
  [YScaleType.TEMPERATURE]: {
    [Enums.SystemUnit.METRIC]: 8,
    [Enums.SystemUnit.IMPERIAL]: 8,
  },
  [YScaleType.RAIN]: {
    [Enums.SystemUnit.METRIC]: 0.5,
    [Enums.SystemUnit.IMPERIAL]: 1,
  },
  [YScaleType.PRESSURE]: {
    [Enums.PressureUnit.MBAR]: 6,
    [Enums.PressureUnit.MERCURY]: 2,
    [Enums.PressureUnit.TORR]: 45,
  },
  [YScaleType.PERCENTAGE]: 25,
  [YScaleType.NOISE]: 40,
  [YScaleType.PPM]: 1000,
};